/**
 * Utility functions for handling epoch time (seconds) to Date conversions
 * Backend now sends createdAt and expiresAt as epoch time in seconds (UTC)
 */

/**
 * Convert epoch time in seconds to Date object
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {Date|null} Date object or null if invalid
 */
export const epochToDate = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return null;
  
  // Convert to number if it's a string
  const epoch = typeof epochSeconds === 'string' ? parseInt(epochSeconds, 10) : epochSeconds;
  
  // Convert seconds to milliseconds for JavaScript Date
  return new Date(epoch * 1000);
};

/**
 * Convert Date object to epoch time in seconds
 * @param {Date} date - Date object
 * @returns {number} Epoch time in seconds
 */
export const dateToEpoch = (date) => {
  if (!date || !(date instanceof Date)) return 0;
  return Math.floor(date.getTime() / 1000);
};

/**
 * Format epoch time for display in user's local timezone
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Formatted date string in local timezone
 */
export const formatEpochDate = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return 'Never';
  
  const date = epochToDate(epochSeconds);
  if (!date) return 'Invalid date';
  
  try {
    // Format in user's local timezone with explicit timezone display
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format epoch time for display with only date (no time) in user's local timezone
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Formatted date string without time
 */
export const formatEpochDateOnly = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return 'Never';
  
  const date = epochToDate(epochSeconds);
  if (!date) return 'Invalid date';
  
  try {
    // Format in user's local timezone with only date
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Get relative time from epoch seconds (e.g., "2 days ago" or "in 5 days")
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Relative time string
 */
export const getRelativeTimeFromEpoch = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return '';
  
  const date = epochToDate(epochSeconds);
  if (!date) return '';
  
  const now = new Date();
  const diffTime = date - now; // Don't use Math.abs to preserve direction
  const isPast = diffTime < 0;
  const absDiffTime = Math.abs(diffTime);
  
  const diffDays = Math.floor(absDiffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(absDiffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(absDiffTime / (1000 * 60));
  
  let timeString = '';
  
  if (diffDays > 0) {
    timeString = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    timeString = `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else if (diffMinutes > 0) {
    timeString = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else {
    return 'Just now';
  }
  
  return isPast ? `${timeString} ago` : `in ${timeString}`;
};

/**
 * Get relative time from epoch seconds showing only days (e.g., "2 days ago" or "in 5 days")
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Relative time string with days only
 */
export const getRelativeTimeFromEpochDaysOnly = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return '';
  
  const date = epochToDate(epochSeconds);
  if (!date) return '';
  
  const now = new Date();
  const diffTime = date - now; // Don't use Math.abs to preserve direction
  const isPast = diffTime < 0;
  const absDiffTime = Math.abs(diffTime);
  
  const diffDays = Math.floor(absDiffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays > 0) {
    const timeString = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    return isPast ? `${timeString} ago` : `in ${timeString}`;
  } else {
    return 'Today';
  }
};

/**
 * Check if epoch time represents an expired date
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {boolean} True if expired, false otherwise
 */
export const isEpochExpired = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return false;
  
  const date = epochToDate(epochSeconds);
  if (!date) return false;
  
  return date < new Date();
};

/**
 * Calculate expiration epoch time from current time + days
 * @param {number} days - Number of days from now
 * @returns {number} Epoch time in seconds
 */
export const calculateExpirationEpoch = (days) => {
  if (!days || days <= 0) return 0;
  
  const expirationDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return dateToEpoch(expirationDate);
};

/**
 * Check if two epoch times are in the same month/year
 * @param {number|string} epochSeconds1 - First epoch time
 * @param {number|string} epochSeconds2 - Second epoch time (optional, defaults to now)
 * @returns {boolean} True if same month/year
 */
export const isSameMonthYear = (epochSeconds1, epochSeconds2 = null) => {
  const date1 = epochToDate(epochSeconds1);
  const date2 = epochSeconds2 ? epochToDate(epochSeconds2) : new Date();
  
  if (!date1 || !date2) return false;
  
  return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
};

/**
 * Format epoch time for dashboard display with specific options in local timezone
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Formatted date string in local timezone
 */
export const formatEpochForDashboard = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return 'N/A';
  
  const date = epochToDate(epochSeconds);
  if (!date) return 'N/A';
  
  try {
    // Use user's locale and timezone
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date for dashboard:', error);
    return 'Invalid date';
  }
};

/**
 * Debug function to log epoch time conversion details
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @param {string} label - Label for debugging
 */
export const debugEpochTime = (epochSeconds, label = 'Debug') => {
  // Debug function - console output removed for production
  return {
    epochSeconds,
    date: epochToDate(epochSeconds),
    formatted: formatEpochDate(epochSeconds),
    relative: getRelativeTimeFromEpoch(epochSeconds),
    isExpired: isEpochExpired(epochSeconds),
    currentTime: new Date(),
    currentEpoch: Math.floor(Date.now() / 1000)
  };
};

/**
 * Get a more detailed relative time description
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Detailed relative time string
 */
export const getDetailedRelativeTime = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return 'Never set';
  
  const date = epochToDate(epochSeconds);
  if (!date) return 'Invalid date';
  
  const now = new Date();
  const diffTime = date - now;
  const isPast = diffTime < 0;
  const absDiffTime = Math.abs(diffTime);
  
  const diffDays = Math.floor(absDiffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((absDiffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMinutes = Math.floor((absDiffTime % (1000 * 60 * 60)) / (1000 * 60));
  
  let parts = [];
  if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
  if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
  if (diffMinutes > 0 && diffDays === 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
  
  if (parts.length === 0) return 'Just now';
  
  const timeString = parts.slice(0, 2).join(' and '); // Show up to 2 units
  return isPast ? `${timeString} ago` : `in ${timeString}`;
};

/**
 * Get a detailed relative time description showing only days
 * @param {number|string} epochSeconds - Epoch time in seconds
 * @returns {string} Detailed relative time string with days only
 */
export const getDetailedRelativeTimeDaysOnly = (epochSeconds) => {
  if (!epochSeconds || epochSeconds === 0) return 'Never set';
  
  const date = epochToDate(epochSeconds);
  if (!date) return 'Invalid date';
  
  const now = new Date();
  const diffTime = date - now;
  const isPast = diffTime < 0;
  const absDiffTime = Math.abs(diffTime);
  
  const diffDays = Math.floor(absDiffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays > 0) {
    const timeString = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    return isPast ? `${timeString} ago` : `in ${timeString}`;
  } else {
    return 'Today';
  }
};
